import { RegistrationAttendeeStatus } from 'constants/rsvp';
import { deleteCall, get, patch, post, put } from '.';
import { serializeDate } from '../utils/date_util';

type RawQuestion = {
    id?: number;
    name?: string;
    definition: { id: number };
    prompt: string;
    responseRequired?: boolean | number;
    checkInDate?: string | null;
    checkOutDate?: string | null;
    options?: string[] | Bizly.LinearQuestionOptions;
    responseCount?: number;
};

type QuestionPayload = {
    id?: number;
    name?: string;
    type: string;
    value?: string | null;
    required?: boolean;
    checkInDate?: string | null;
    checkOutDate?: string | null;
    isSeeded?: boolean;
    options?: string[] | Bizly.LinearScaleValuesType;
};

export const loadParcels = (id: number) =>
    get(`events/${id}/parcels`).then(json =>
        json.parcels.map((p: Bizly.Parcel) => ({
            ...p,
            recipients: p.recipients || [],
        }))
    );

// TODO: this currently loads all parcels, change to use an endpoint specifically for loading parcel of id
export const loadParcel = (eventId: number, parcelId: number) =>
    loadParcels(eventId).then(parcels => parcels.find((p: Bizly.Parcel) => p.id === parcelId));

export const createParcel = (eventId: number, parcel: Partial<Bizly.Parcel>) =>
    post(`events/${eventId}/parcels`, parcel);

export const updateParcel = (eventId: number, parcel: Bizly.Parcel) =>
    put(`events/${eventId}/parcels/${parcel.id}`, parcel);

export const updateParcelRecipients = (eventId: number, parcelId: number, recipients: number[]) =>
    patch(`events/${eventId}/parcels/${parcelId}/recipients`, { recipients });

export const deleteParcel = (eventId: number, parcel: Bizly.Parcel) =>
    deleteCall(`events/${eventId}/parcels/${parcel.id}`);

export const sendParcel = (eventId: number | string, parcelId: number) =>
    post(`events/${eventId}/parcels/${parcelId}/send`);

export const registrationPagePublish = (eventId: number, parcelId: number) =>
    post(`events/${eventId}/parcels/${parcelId}/registration-page/publish`);

export const registrationPageUnpublish = (eventId: number, parcelId: number) =>
    post(`events/${eventId}/parcels/${parcelId}/registration-page/unpublish`);

export type ParcelUpdateNotificationReason = 'details_changed' | 'update_rsvp';

/** Emails everyone who has been sent this invite (or registered through its public link). */
export const notifyParcelUpdate = (
    eventId: number,
    parcelId: number,
    body: { reason: ParcelUpdateNotificationReason; message?: string }
) => post(`events/${eventId}/parcels/${parcelId}/notify-update`, body) as Promise<{ parcel: Bizly.Parcel }>;

export const copyParcel = (parcelId: number) => post(`parcels/${parcelId}/copy`);

export const questionDefinitionIds: Record<string, number> = {
    dietaryRestrictions: 1,
    foodAllergies: 2,
    travelDates: 3,
    logistics: 4,
    attendanceType: 11,
    text: 5,
    multipleChoice: 6,
    multipleChoiceMultipleAnswers: 7,
    date: 8,
    linearScale: 9,
};

const questionDefinitionIdForType = (type: string) => questionDefinitionIds[type];

const isValidQuestion = (question: { type?: string }) =>
    !!question.type && Object.keys(questionDefinitionIds).includes(question.type) && question.type !== 'foodAllergies';

const serializeQuestion = (question: RawQuestion) => {
    const { checkInDate, checkOutDate, definition, prompt, responseRequired, ...isomorphicProps } = question;
    return {
        ...isomorphicProps,
        checkInDate: serializeDate(checkInDate ?? undefined),
        checkOutDate: serializeDate(checkOutDate ?? undefined),
        isSeeded: definition.id < 5 || definition.id === 11,
        type: Object.keys(questionDefinitionIds).find(key => questionDefinitionIds[key] === definition.id),
        value: prompt,
        required: Boolean(responseRequired),
    };
};

const deserializeQuestion = (question: QuestionPayload) => {
    const { checkInDate, checkOutDate, required, type, value, ...isomorphicProps } = question;
    return {
        ...isomorphicProps,
        checkInDate: serializeDate(checkInDate ?? undefined),
        checkOutDate: serializeDate(checkOutDate ?? undefined),
        prompt: value,
        responseRequired: required ? 1 : 0,
        definitionId: questionDefinitionIdForType(type),
    };
};

export const loadQuestions = (id: number) =>
    get(`parcels/${id}/questions`)
        .then(json => json.questions.map(serializeQuestion))
        .then(questionsToValidate => questionsToValidate.filter(isValidQuestion));

export const createQuestion = (parcelId: number, question: QuestionPayload) =>
    post(`parcels/${parcelId}/questions`, deserializeQuestion(question)).then(json => serializeQuestion(json.question));

/** Saves a planner's drag-and-drop order; questions left out keep their order after these. */
export const reorderQuestions = (parcelId: number, questionIds: number[]) =>
    put(`parcels/${parcelId}/questions/order`, { questionIds });

export const deleteQuestion = (parcelId: number, questionId: number) =>
    deleteCall(`parcels/${parcelId}/questions/${questionId}`);

export const updateQuestion = (parcelId: number, question: QuestionPayload) =>
    put(`parcels/${parcelId}/questions/${question.id}`, deserializeQuestion(question)).then(json =>
        serializeQuestion(json.question)
    );

type RoomTypePayload = {
    name: string;
    rate?: string | null;
    nights: Bizly.ParcelRoomTypeNight[];
};

/**
 * Room types have no GET endpoint of their own — `store`/`update`/`destroy` each return the
 * *whole* parcel (via bizly-api's `ParcelResponse`, `room_types` nested inside it), not a bare
 * room type. The shared `call()` wrapper's snake_case/camelCase conversion already handles the
 * casing on both sides, so no manual (de)serialization is needed here, unlike Questions' field
 * remapping.
 */
export const createRoomType = (parcelId: number, roomType: RoomTypePayload): Promise<{ parcel: Bizly.Parcel }> =>
    post(`parcels/${parcelId}/room-types`, roomType, { showErrorCode: true });

export const updateRoomType = (
    parcelId: number,
    roomType: RoomTypePayload & { id: number }
): Promise<{ parcel: Bizly.Parcel }> => {
    // Picked field by field: callers pass UI rows that carry client-only keys such as `tempId`.
    const { id, name, rate, nights } = roomType;
    return put(`parcels/${parcelId}/room-types/${id}`, { name, rate, nights }, { showErrorCode: true });
};

export const deleteRoomType = (parcelId: number, roomTypeId: number): Promise<{ parcel: Bizly.Parcel }> =>
    deleteCall(`parcels/${parcelId}/room-types/${roomTypeId}`);

/**
 * Whether a failed room-type create/update was refused because one of its nights falls outside
 * the event's dates (a week of slack either side) — e.g. the event was rescheduled after this
 * room type's nights were saved. bizly-api's validation handler doesn't key errors by field: a
 * `ValidationException` arrives as `errors: { code, messages: "<string>", display_message }`, so
 * the check reads the message `ValidatesNightsWithinEventDates` writes.
 */
const NIGHTS_OUT_OF_RANGE_PATTERN = /^Night must fall within/;

export function isRoomTypeNightsDateError(error: unknown): error is BizlyAPIError.RoomTypeNightsDate {
    const expectedError = error as BizlyAPIError.RoomTypeNightsDate;
    if (expectedError?.code !== 422) return false;

    const { messages, displayMessage } = expectedError.raw?.errors ?? {};
    return [messages, displayMessage].flat().some(text => NIGHTS_OUT_OF_RANGE_PATTERN.test(text ?? ''));
}

export const getRSVPInfo = (eventId: number, query: string): Promise<Bizly.RsvpInfo> =>
    get(`events/${eventId}/rsvp${query}`, { requireToken: false });

export const getPlaybookRSVPPreview = (playbookId: string, parcelId: number) =>
    get(`playbooks/${playbookId}/rsvp-preview/${parcelId}`, { requireToken: false });

export const getRSVPPreview = (eventId: number, query?: string) =>
    get(`events/${eventId}/rsvp-preview${query}`, { requireToken: false });

export type RsvpSubmitResponse = {
    success?: boolean;
    attendee?: { status?: RegistrationAttendeeStatus };
    /** Present when the submission picked rooms; `message` is set when the block ran short. */
    roomRequest?: { status: Bizly.RoomRequestStatus; message: string | null };
};

export const updateRSVPInfo = (
    eventId: number,
    query: string,
    response: Partial<Bizly.EventAttendee>
): Promise<RsvpSubmitResponse> => post(`events/${eventId}/rsvp/${query}`, response, { requireToken: false });
